import { TransactionListRow } from "../../types/sale.type";
import {
  formatDateParam,
  getDefaultDateRange,
  resolveInitialDateRange,
} from "../ETax/dateRangeParams";
import {
  ALL_OPTION,
  DEFAULT_PAGE_SIZE,
  PAGE_SIZE_OPTIONS,
  PAYMENT_CHANNEL_FILTER,
  STATUS_FILTER,
} from "./data";
import { BILL_NUMBER_MAX_LENGTH, SalesListFilters } from "./salesListFilters";

// How การขาย is addressed by URL. The list keeps its filters, page and page size in the query
// string, so returning to it within the app (the browser's Back button) reopens it exactly as it
// was left. A bill opened from the list carries that query string along as a return ticket, so
// the bill page's การขาย breadcrumb leads back to the same list rather than a reset one. A full
// page load starts clean: src/services/keycloak.ts drops the query string after sign-in.

const SALES_LIST_PATH = "/sales";

/**
 * Query-param names of the list. `status` and `replace_flagged` are older deep links (the
 * dashboard opens `?status=pending_cancel`) and keep their meaning.
 */
const PARAM = {
  dateFrom: "date_from",
  dateTo: "date_to",
  companyId: "company_id",
  routeCode: "route_code",
  transactionNo: "transaction_no",
  status: "status",
  paymentType: "payment_type",
  taxId: "tax_id",
  customerName: "customer_name",
  customerCode: "customer_code",
  replaceFlagged: "replace_flagged",
  page: "page",
  limit: "limit",
} as const;

/** The bill pages' query param holding the query string of the list the bill was opened from. */
const RETURN_TICKET_PARAM = "list_query";

const FLAG_ON = "true";

/** company_id is a bigint; any other value would fail the list request instead of filtering it. */
const COMPANY_ID_PATTERN = /^\d+$/;

export const FIRST_PAGE = 1;

export interface SalesListUrlState {
  filters: SalesListFilters;
  page: number;
  limit: number;
}

const oneOf = (
  value: string | null,
  options: readonly { value: string }[],
): string =>
  value && options.some((option) => option.value === value) ? value : ALL_OPTION;

const parsePage = (value: string | null): number => {
  const page = Number(value);
  return Number.isSafeInteger(page) && page >= FIRST_PAGE ? page : FIRST_PAGE;
};

/** A bill number longer than the column allows cannot match any bill, so it is dropped. */
const parseBillNumber = (value: string | null): string => {
  const transactionNo = value?.trim() ?? "";
  return transactionNo.length <= BILL_NUMBER_MAX_LENGTH ? transactionNo : "";
};

/** A page size is accepted only when the pagination offers it. */
const parseLimit = (value: string | null): number => {
  const limit = Number(value);
  return PAGE_SIZE_OPTIONS.includes(limit) ? limit : DEFAULT_PAGE_SIZE;
};

/**
 * The list state a query string describes. Anything unknown or malformed falls back to the
 * default for that field, so a hand-edited or stale link still opens a working list.
 */
export const readSalesListUrlState = (
  params: URLSearchParams,
  canPickBranch: boolean,
): SalesListUrlState => {
  const dateRange = resolveInitialDateRange(
    params.get(PARAM.dateFrom),
    params.get(PARAM.dateTo),
  );
  const companyIdParam = params.get(PARAM.companyId) ?? "";
  // สาขา and รหัส are head-office filters. Any other role has no control for them (the API pins
  // it to its own branch), so a value in its URL is dropped rather than applied unseen.
  const companyId =
    canPickBranch && COMPANY_ID_PATTERN.test(companyIdParam)
      ? companyIdParam
      : ALL_OPTION;
  // Head office picks a สายส่ง within a สาขา, so a route without one is dropped as well. The
  // route options load asynchronously, so the code itself is taken as given.
  const isRouteSelectable = !canPickBranch || companyId !== ALL_OPTION;
  return {
    filters: {
      dateFrom: dateRange.from,
      dateTo: dateRange.to,
      companyId,
      routeCode: (isRouteSelectable && params.get(PARAM.routeCode)) || ALL_OPTION,
      transactionNo: parseBillNumber(params.get(PARAM.transactionNo)),
      status: oneOf(params.get(PARAM.status), STATUS_FILTER),
      paymentType: oneOf(params.get(PARAM.paymentType), PAYMENT_CHANNEL_FILTER),
      taxId: params.get(PARAM.taxId) ?? "",
      customerName: params.get(PARAM.customerName) ?? "",
      customerCode: canPickBranch ? params.get(PARAM.customerCode) ?? "" : "",
      replaceFlagged: params.get(PARAM.replaceFlagged) === FLAG_ON,
    },
    page: parsePage(params.get(PARAM.page)),
    limit: parseLimit(params.get(PARAM.limit)),
  };
};

/** The query string of a list state. Defaults are left out, so an untouched list stays `/sales`. */
export const writeSalesListUrlState = ({
  filters,
  page,
  limit,
}: SalesListUrlState): URLSearchParams => {
  const params = new URLSearchParams();
  // An empty value is never written either: it means "no filter" for every field.
  const setUnlessDefault = (
    key: string,
    value: string | null,
    defaultValue: string | null,
  ) => {
    if (value && value !== defaultValue) params.set(key, value);
  };
  const defaultRange = getDefaultDateRange();
  setUnlessDefault(
    PARAM.dateFrom,
    formatDateParam(filters.dateFrom),
    formatDateParam(defaultRange.from),
  );
  setUnlessDefault(
    PARAM.dateTo,
    formatDateParam(filters.dateTo),
    formatDateParam(defaultRange.to),
  );
  setUnlessDefault(PARAM.companyId, filters.companyId, ALL_OPTION);
  setUnlessDefault(PARAM.routeCode, filters.routeCode, ALL_OPTION);
  setUnlessDefault(PARAM.transactionNo, filters.transactionNo.trim(), null);
  setUnlessDefault(PARAM.status, filters.status, ALL_OPTION);
  setUnlessDefault(PARAM.paymentType, filters.paymentType, ALL_OPTION);
  setUnlessDefault(PARAM.taxId, filters.taxId, null);
  setUnlessDefault(PARAM.customerName, filters.customerName, null);
  setUnlessDefault(PARAM.customerCode, filters.customerCode, null);
  setUnlessDefault(PARAM.replaceFlagged, filters.replaceFlagged ? FLAG_ON : null, null);
  setUnlessDefault(PARAM.page, String(page), String(FIRST_PAGE));
  setUnlessDefault(PARAM.limit, String(limit), String(DEFAULT_PAGE_SIZE));
  return params;
};

/** `search` is `URLSearchParams.toString()` output: no leading "?". */
const withQuery = (path: string, search: string): string =>
  search ? `${path}?${search}` : path;

/** The bill page of a list row, carrying the list's query string as its return ticket. */
export const billDetailPath = (
  bill: Pick<TransactionListRow, "uuid" | "transaction_type">,
  listSearch: string,
): string => {
  const path =
    bill.transaction_type === "cashsale"
      ? `/sales/cash-sales/details/${bill.uuid}`
      : `/sales/invoice/details/${bill.uuid}`;
  const ticket = listSearch
    ? new URLSearchParams({ [RETURN_TICKET_PARAM]: listSearch }).toString()
    : "";
  return withQuery(path, ticket);
};

/**
 * Where a bill page's การขาย breadcrumb leads: back to the list the bill was opened from, with
 * its filters and page, or to the plain list when the bill was opened anywhere else. The ticket
 * is re-serialised, so whatever it holds can only ever become `/sales`'s own query string.
 */
export const salesListReturnPath = (billParams: URLSearchParams): string =>
  withQuery(
    SALES_LIST_PATH,
    new URLSearchParams(billParams.get(RETURN_TICKET_PARAM) ?? "").toString(),
  );
